import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./assest_design/SmartChatbot.css";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8787"
    : "https://procode-chatbot.procodeza.workers.dev";

export default function SmartChatbot() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [leadData, setLeadData] = useState({
    name: "",
    company: "",
    need: "",
    urgency: "",
    budget: "",
    timeline: "",
    clientType: "",
    sourcePage: "",
  });
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  useEffect(() => {
    if (!open) return;

    setMessages([
      {
        role: "bot",
        text: getGreetingForPage(location.pathname),
        options: getOptionsForPage(location.pathname),
      },
    ]);
  }, [open, location.pathname]);

  const performAction = (action) => {
    if (!action) return;

    if (action.type === "navigate" && action.to) {
      navigate(action.to);
      return;
    }

    if (action.type === "scroll" && action.target) {
      const el = document.getElementById(action.target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleSend = async (textOverride = null) => {
    const userText = (textOverride ?? input).trim();
    if (!userText || loading) return;

    const userMessage = { role: "user", text: userText };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      console.log("Chatbot request to:", `${API_BASE}/api/chat`);

      const response = await fetch(`${API_BASE}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: userText,
          path: location.pathname,
          history: messages,
          leadData,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Chatbot API error:", response.status, errorText);
        throw new Error(`API error ${response.status}`);
      }

      const data = await response.json();
      console.log("Chatbot API success:", data);

      if (data.leadData) {
        setLeadData(data.leadData);
      }

      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: data.reply,
          options: buildFollowUpOptions(data.intent),
        },
      ]);

      if (data.action) {
        setTimeout(() => {
          performAction(data.action);
        }, 500);
      }
    } catch (error) {
      console.error("Chatbot fetch failed:", error);

      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text:
            "I couldn’t reach the assistant right now, but I can still guide you to pricing, examples, support or contact.",
          options: [
            { label: "Get pricing", value: "I need pricing" },
            { label: "See examples", value: "Show me examples" },
            {
              label: "Fix an existing system",
              value: "I need support for an existing system",
            },
            { label: "Contact the team", value: "I want to contact the team" },
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        className={`chatbot-fab ${open ? "open" : ""}`}
        onClick={() => setOpen(!open)}
        title="Chat with Yunus"
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C6.48 2 2 6.12 2 11.2c0 2.7 1.35 5.15 3.5 6.8V22l3.6-2c.95.25 1.95.38 2.9.38 5.52 0 10-4.12 10-9.2S17.52 2 12 2Z"
            fill="white"
          />
        </svg>
      </button>

      {open && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div>
              <strong>Yunus</strong>
              <span>ProCode AI Assistant</span>
            </div>
            <button onClick={() => setOpen(false)}>✕</button>
          </div>

          <div className="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chatbot-msg ${m.role}`}>
                <div>{m.text}</div>

                {m.options && m.options.length > 0 && (
                  <div className="chatbot-options">
                    {m.options.map((opt, idx) => (
                      <button key={idx} onClick={() => handleSend(opt.value)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="chatbot-msg bot">
                <div>Thinking…</div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          <div className="chatbot-input">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about pricing, PRISM, AI, websites, support..."
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
            />
            <button onClick={() => handleSend()} disabled={loading}>
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function getGreetingForPage(path) {
  switch (path) {
    case "/proquote":
      return "I can help you scope your project before you generate an estimate. Are you looking for a website, internal system, AI solution, branding support, or something else?";
    case "/innovation":
      return "I can explain our innovation work, including PRISM, mining tracking, hydroponics and rural learning projects. What would you like to explore?";
    case "/contact":
      return "I can help you decide whether you should request a quote, ask for support, or contact the team directly.";
    case "/marketing":
      return "I can help you explore ProCode ZA’s marketing, PR, SEO and brand growth support. What do you want to know?";
    case "/pod-programme":
      return "I can explain how the Pod Programme supports long-term system continuity, support and legacy takeovers.";
    default:
      return "What are you trying to build, improve or fix today?";
  }
}

function getOptionsForPage(path) {
  if (path === "/innovation") {
    return [
      { label: "Tell me about PRISM", value: "Tell me about PRISM" },
      { label: "Show AI examples", value: "Show me your AI examples" },
      {
        label: "What industries do you support?",
        value: "What industries do you support?",
      },
      { label: "I want similar work", value: "I want something similar built" },
    ];
  }

  if (path === "/contact") {
    return [
      { label: "I need pricing", value: "I need pricing" },
      {
        label: "I need urgent support",
        value: "I need support for an existing system urgently",
      },
      { label: "I want to build a platform", value: "I want to build a platform" },
      {
        label: "I already have a quote",
        value: "I already have a quote from another provider",
      },
    ];
  }

  if (path === "/marketing") {
    return [
      { label: "What marketing services do you offer?", value: "What marketing services do you offer?" },
      { label: "Do you do SEO?", value: "Do you do SEO?" },
      { label: "I need a campaign quote", value: "I need pricing for marketing support" },
      { label: "I need PR support", value: "I need PR support" },
    ];
  }

  if (path === "/pod-programme") {
    return [
      { label: "What is the Pod Programme?", value: "What is the Pod Programme?" },
      { label: "Can you support an old system?", value: "Can you support an existing system built by someone else?" },
      { label: "How much does Pod cost?", value: "How much does Pod cost?" },
      { label: "I need a quote", value: "I need pricing" },
    ];
  }

  return [
    { label: "I need pricing", value: "I need pricing" },
    { label: "I want examples", value: "Show me examples" },
    { label: "I need a website", value: "I need a website" },
    { label: "I need support", value: "I need help fixing an existing system" },
  ];
}

function buildFollowUpOptions(intent) {
  if (intent === "off_domain") {
    return [
      { label: "What services do you offer?", value: "What services do you offer?" },
      { label: "Tell me about PRISM", value: "Tell me about PRISM" },
      { label: "I need pricing", value: "I need pricing" },
      { label: "I need support", value: "I need support for an existing system" },
    ];
  }

  if (intent === "pricing") {
    return [
      { label: "Open ProQuote", value: "I need pricing" },
      { label: "I need a website", value: "I need a website" },
      { label: "I need a system", value: "I need an internal system" },
    ];
  }

  if (intent === "prism") {
    return [
      { label: "Tell me about PRISM", value: "Tell me about PRISM" },
      { label: "Show more examples", value: "Show me examples" },
      { label: "I want similar work", value: "I want something similar built" },
    ];
  }

  if (intent === "marketing") {
    return [
      { label: "Marketing services", value: "What marketing services do you offer?" },
      { label: "SEO support", value: "Do you do SEO?" },
      { label: "Get a quote", value: "I need pricing for marketing support" },
    ];
  }

  if (intent === "pod") {
    return [
      { label: "What is Pod?", value: "What is the Pod Programme?" },
      { label: "Legacy takeover", value: "Can you support an existing system built by someone else?" },
      { label: "Get pricing", value: "I need pricing" },
    ];
  }

  if (intent === "support") {
    return [
      { label: "Contact the team", value: "I want to contact the team" },
      { label: "Get a quote", value: "I need pricing" },
    ];
  }

  return [
    { label: "Pricing", value: "I need pricing" },
    { label: "Examples", value: "Show me examples" },
    { label: "Contact", value: "I want to contact the team" },
  ];
}